import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getSmsDashboardConversationsKey } from './useConversations';

import type {
  IConversation,
  IConversationsListPages,
} from 'infrastructure/interfaces';
import type { RootState } from 'store';

interface ISocketData {
  conversationId: number;
  assignedAt: number | null;
  assignedByFullname: string;
  assignedByLogin: string;
}

export const useUpdateAssignConversationState = () => {
  const queryClient = useQueryClient();
  const { conversationType, filters } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const updateAssignConversationState = (socketData: ISocketData) => {
    const conversationKey = getSmsDashboardConversationsKey({
      type: conversationType,
      read: filters.read,
      closed: filters.closed,
      lastMessageDate: filters.lastMessageDate,
      searchQuery: filters.searchQuery,
      assignedToMe: false,
    });

    const conversations =
      queryClient.getQueryData<IConversationsListPages>(conversationKey);

    if (!conversations) return;

    const updatedPages = conversations.pages.map((page) => {
      const updatedItems = page.items.reduce<IConversation[]>((acc, item) => {
        if (item.id !== socketData.conversationId) {
          acc.push(item);
          return acc;
        }

        acc.push({
          ...item,
          assignedAt: socketData.assignedAt
            ? new Date(socketData.assignedAt).toISOString()
            : null,
          assignedByFullname: socketData.assignedByFullname,
          assignedByLogin: socketData.assignedByLogin,
        });

        return acc;
      }, []);

      return { ...page, items: updatedItems };
    });

    queryClient.setQueryData<IConversationsListPages>(conversationKey, (old) =>
      old ? { ...old, pages: updatedPages } : old,
    );
  };

  return { updateAssignConversationState };
};
